import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

const DEBOUNCE_WAIT = 250;

class InfiniteScroll extends React.Component {
  constructor(props) {
    super(props);
    this.wrapperRef = undefined;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.calculateTopOffset = this.calculateTopOffset.bind(this);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.debouncedHandleScroll = _.debounce(this.handleScroll, DEBOUNCE_WAIT);
    this.debouncedResize = _.debounce(this.handleResize, DEBOUNCE_WAIT);
    this.state = {
      width: undefined,
    };
  }

  componentDidMount() {
    if (this.props.hasMore) {
      window.addEventListener('scroll', this.debouncedHandleScroll, { passive: true });
      window.addEventListener('resize', this.debouncedResize, { passive: true });

      this.setState({ width: this.wrapperRef.offsetWidth });
    }

    if (NGX.App.state.framed && this.props.hasMore) {
      const eventService = NGXD.getServiceContainer().getService('event');
      eventService.listen('parent:event:scroll', this.handleScroll);
    }
  }

  componentDidUpdate() {
    // resolves intermittent issue on MS Edge where masonry container has slighlty different width after layout is completed
    if (this.wrapperRef.offsetWidth !== this.state.width) {
      this.setState({ width: this.wrapperRef.offsetWidth });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.debouncedHandleScroll, { passive: true });
    window.removeEventListener('resize', this.debouncedResize, { passive: true });

    const eventService = NGXD.getServiceContainer().getService('event');
    eventService.unlisten('parent:event:scroll', this.handleScroll);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.hasMore && !nextProps.hasMore) {
      window.removeEventListener('scroll', this.debouncedHandleScroll, { passive: true });
    }
  }

  handleResize(e) {
    if (this.wrapperRef.offsetWidth !== this.state.width) {
      this.setState({ width: this.wrapperRef.offsetWidth });
    }
  }

  handleScroll({ offsetTop, scrollTop, clientHeight }) {
    // prevent loading of more content to fill width when resizing
    if (!this.wrapperRef) return;
    if (!!this.state.width && this.wrapperRef.offsetWidth !== this.state.width) return;

    let _offsetTop; // space between module and top of page or space between iframe + module and top of page
    let _scrollTop; // how many vertically scrolled px from top of window
    let _clientHeight; // the height of the container that is scrolling

    // params only present when a scroll event was triggered from a responsive embed
    const isResponsiveEmbed = !_.isNil(offsetTop);

    if (isResponsiveEmbed) {
      _offsetTop = offsetTop + this.calculateTopOffset(this.wrapperRef);
      _scrollTop = scrollTop;
      _clientHeight = clientHeight;
    } else {
      _offsetTop = this.calculateTopOffset(this.wrapperRef);
      _scrollTop = !_.isNil(window.pageYOffset) ?
        window.pageYOffset :
        (document.documentElement || document.body.parentNode || document.body).scrollTop;
      _clientHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    }

    const pxFromBottom = _offsetTop + this.wrapperRef.offsetHeight - _scrollTop - _clientHeight;

    // wrapperRef will have zero height if initially mounted out of viewport
    if (this.wrapperRef.offsetHeight > 0 && pxFromBottom < this.props.threshold && this.props.hasMore) {
      this.props.onLoadMore();
    }
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  calculateTopOffset(node) {
    if (!node) return 0;
    return node.offsetTop + this.calculateTopOffset(node.offsetParent);
  }

  render() {
    return <div ref={this.setWrapperRef}>{this.props.children}</div>;
  }
}

InfiniteScroll.PropTypes = {
  children:   PropTypes.node.isRequired,
  hasMore:    PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  threshold:  PropTypes.number, // (px)
};

InfiniteScroll.defaultProps = {
  hasMore:   false,
  threshold: 200,
};

export default InfiniteScroll;
